'use client'

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useSession } from '@rewardops/auth'

import { useApi } from '~/components/hooks/use-api'

type CartContext = {
  getCartQuantity: () => number
  resetCartQuantity: () => void
}

const defaultCartContext: CartContext = {
  getCartQuantity: () => 0,
  resetCartQuantity: () => {},
}

const CartContext = createContext<CartContext>(defaultCartContext)

export function useCart() {
  return useContext(CartContext)
}

// Dummy provider to use when cart is disabled
const NoopCartProvider = ({ children }: { children: ReactNode }) => (
  <CartContext.Provider value={defaultCartContext}>
    {children}
  </CartContext.Provider>
)

export function CartProvider({ children }: { children: ReactNode }) {
  const { tenant } = useApi()
  const { data: session } = useSession()

  if (!tenant.flags.shoppingCart) {
    return <NoopCartProvider>{children}</NoopCartProvider>
  }

  const { api } = useApi()
  const { data: cartItems } = api.cart.getCartItems.useQuery(undefined, {
    enabled: !!session,
    staleTime: 5000,
  })
  const [localCartQuantity, setLocalCartQuantity] = useState<number | null>(
    null,
  )

  // Update local state when cart items change
  useEffect(() => {
    if (cartItems?.totalCartItemsQuantity !== undefined) {
      setLocalCartQuantity(cartItems.totalCartItemsQuantity)
    }
  }, [cartItems?.totalCartItemsQuantity])

  const getCartQuantity = () => {
    return localCartQuantity !== null
      ? localCartQuantity
      : cartItems?.totalCartItemsQuantity || 0
  }

  const resetCartQuantity = () => {
    setLocalCartQuantity(0)
  }

  return (
    <CartContext.Provider
      value={{
        getCartQuantity,
        resetCartQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
